import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { InvoiceModel, InvoiceSource } from '@app/+authenticated/+account/billing/billing.model';
import { MoneyPipe } from '@app/pipes/money.pipe';
import { AccountApi } from '@app/reducers/account/account.api';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { hasPermission } from '@sb/shared/permission/util';
import { ButtonComponent } from '@sb/ui/button';
import { CardComponent, CardContentComponent, CardHeaderComponent, CardTitleDirective } from '@sb/ui/card';
import { LaddaDirective } from '@sb/ui/ladda';
import { LogoComponent } from '@sb/ui/logo';
import { of as observableOf, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { AccountService } from '../../reducers/account/account.service';
import { AuthService } from '../../reducers/auth/auth.service';
import { getPermissionState } from '../../reducers/auth/permission.helper';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'locked',
  imports: [
    NgClass,
    TranslatePipe,
    MoneyPipe,
    LogoComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleDirective,
    CardContentComponent,
    ButtonComponent,
    LaddaDirective,
  ],
  templateUrl: './locked.component.html',
})
export class LockedComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private store = inject<Store<AppState>>(Store);
  private accountService = inject(AccountService);
  private cd = inject(ChangeDetectorRef);
  private api = inject(AccountApi);

  unpaidInvoices: InvoiceModel[] = [];
  mode: 'manager' | 'employee';

  private dataSubs = new Subscription();

  isInvoicePaymentPageLoading: { [key: string]: boolean } = {};

  ngOnInit() {
    this.dataSubs.add(
      this.store
        .select(getPermissionState)
        .pipe(
          switchMap((permissionState) => {
            const manageAccount = hasPermission(
              {
                permissions: ['Manage account'],
                userId: 'me',
                departments: 'any',
              },
              permissionState,
            );

            if (manageAccount) {
              return this.accountService.fetchExpiredInvoices();
            } else {
              this.mode = 'employee';
              return observableOf(false);
            }
          }),
          take(1),
        )
        .subscribe({
          next: (invoices: InvoiceModel[]) => {
            if (invoices) {
              this.mode = 'manager';
              this.unpaidInvoices = invoices.filter((invoice) => invoice.status !== 'paid');
              if (this.unpaidInvoices.length > 0) return;
            }
            this.router.navigate(['dashboard']);
          },
        }),
    );
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  backToLogin() {
    this.authService.logout().then(() => {
      this.router.navigate(['login']);
    });
  }

  openInvoice(invoice: InvoiceModel): void {
    if (invoice.source === InvoiceSource.MONEYBIRD && invoice.payment_url) {
      window.open(invoice?.payment_url, '_blank');
    }

    if (invoice.source === InvoiceSource.CHARGEBEE && invoice.status !== 'paid') {
      this.isInvoicePaymentPageLoading[invoice.id] = true;
      this.cd.detectChanges();
      this.dataSubs.add(
        this.api.fetchChargebeePaymentPage().subscribe({
          next: (paymentPage) => {
            this.isInvoicePaymentPageLoading[invoice.id] = false;
            this.cd.detectChanges();
            window.open(paymentPage.hosted_page.url, '_blank');
          },
        }),
      );
    }

    this.dataSubs.add(
      this.api.fetchInvoicePdf(invoice.id).subscribe((invoicePdf) => {
        window.open(invoicePdf.url, '_blank');
      }),
    );
  }
}
